import { AlertDialogHeader, AlertDialogFooter } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { BridgeRoute, useDeleteRoute } from "@/lib/api/edgelink";
import { AlertDialog, AlertDialogContent, AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction } from "@radix-ui/react-alert-dialog";
import { Time } from '@/components/ui/time';
import { Plus, Edit, Trash2, Loader2, Route } from "lucide-react";
import { useState } from "react";
import UpsertRouteDialog from "./UpsertRouteConfig";

export default function BridgeRoutesTab({ routes, bridgeId }: { routes: BridgeRoute[]; bridgeId: string }) {
  const [upsertOpen, setUpsertOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState<BridgeRoute | undefined>(undefined);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const deleteRoute = useDeleteRoute({
    onSuccess: () => setDeleteId(null),
  });

  const handleEdit = (route: BridgeRoute) => {
    setEditingRoute(route);
    setUpsertOpen(true);
  };

  const handleAdd = () => {
    setEditingRoute(undefined);
    setUpsertOpen(true);
  };

  const handleDelete = async () => {
    if (deleteId) {
      await deleteRoute.mutateAsync({ bridgeId, routeId: deleteId });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={handleAdd} size="sm" className="gap-2">
          <Plus className="h-4 w-4" />
          Add Route
        </Button>
      </div>

      {routes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center border rounded-lg border-dashed">
          <div className="rounded-full bg-muted/20 p-4 mb-4">
            <Route className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold mb-2">No Routes Configured</h3>
          <p className="text-muted-foreground max-w-sm mb-4">
            Add a route to define network ranges accessible through this bridge.
          </p>
          <Button variant="outline" onClick={handleAdd}>
            Add First Route
          </Button>
        </div>
      ) : (
        <div className="grid gap-3">
          {routes.map((route) => (
            <Card key={route.id} className="bg-card2/50">
              <CardContent className="flex items-center justify-between p-4">
                <div className="flex items-center gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-background border">
                    <Route className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="font-mono font-semibold text-lg">{route.edgeAddressRangeCidr}</p>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                       <span>Created <Time date={route.createdAt} format="date" noTooltip /></span>
                       {route.namespaceId && <span className="font-mono text-xs opacity-50">ID: {route.namespaceId.substring(0, 8)}...</span>}
                       {route.mappedAddressRange && (
                         <span className="flex items-center gap-1.5 ml-2 border-l pl-3 border-border/50">
                           <span className="text-xs text-muted-foreground">Mapped to:</span>
                           <span className="font-mono text-xs bg-muted/30 px-1.5 py-0.5 rounded text-foreground">{route.mappedAddressRange}</span>
                         </span>
                       )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                   <Button variant="ghost" size="icon" onClick={() => handleEdit(route)}>
                     <Edit className="h-4 w-4 text-muted-foreground" />
                   </Button>
                   <Button 
                     variant="ghost" 
                     size="icon" 
                     className="hover:text-destructive"
                     onClick={() => setDeleteId(route.id)}
                   >
                     <Trash2 className="h-4 w-4" />
                   </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <UpsertRouteDialog
        bridgeId={bridgeId}
        route={editingRoute}
        open={upsertOpen}
        onOpenChange={(open) => {
          setUpsertOpen(open);
          if (!open) setEditingRoute(undefined);
        }}
      />

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Route?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the route. Devices relying on this route may lose connectivity.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteRoute.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Delete Route
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}